/**
 * A live approximation of the server's anonymized version, for the editor's preview while the case has unsaved
 * edits: every client name or alias, as a whole word, in any case and with or without accents, becomes the label
 * (a possessive keeps its "'s"). The server's version (`GET /api/cases/{id}/anonymized`) is the one that is shared.
 */

/** Letters without their accents, one code unit for one, so positions in the folded text match the original. */
function fold(text: string): string {
  return text
    .split('')
    .map((ch) => {
      const base = ch.normalize('NFD').replace(/\p{M}/gu, '');
      return base.length === 1 ? base : ch;
    })
    .join('')
    .toLowerCase();
}

const WORD = /[\p{L}\p{N}]/u;
const isWord = (ch: string | undefined) => ch !== undefined && WORD.test(ch);

/** Whether a name found at `at` stands as a whole word (a possessive "'s" after it still counts). */
function wholeWord(folded: string, at: number, length: number): boolean {
  return !isWord(folded[at - 1]) && !isWord(folded[at + length]);
}

/** The earliest whole-word match of any name at or after `from`; names come longest first, so at one place the longest wins. */
function nextMatch(folded: string, names: string[], from: number): { at: number; length: number } | null {
  let best: { at: number; length: number } | null = null;
  for (const name of names) {
    for (let at = folded.indexOf(name, from); at !== -1 && (!best || at < best.at); at = folded.indexOf(name, at + 1)) {
      if (wholeWord(folded, at, name.length)) {
        best = { at, length: name.length };
        break;
      }
    }
  }
  return best;
}

export function anonymize(text: string, names: string[], label: string): string {
  const wanted = [...new Set(names.map((n) => fold(n.trim())).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (wanted.length === 0 || !text) return text;
  const folded = fold(text);
  let out = '';
  let from = 0;
  for (let match = nextMatch(folded, wanted, 0); match; match = nextMatch(folded, wanted, from)) {
    out += text.slice(from, match.at) + label;
    from = match.at + match.length;
  }
  return out + text.slice(from);
}
